import React, { useState, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { useCompanyIncomeStatement } from "../hooks/useCompanyIncomeStatement";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { getChartColors } from "../constants/chartColors";
import {
  TransformedDataPoint,
  calculateQuarterlyGrowthMetrics,
  calculateAnnualGrowthMetrics,
} from "../utils/chartDataTransformers";
import {
  ViewMode,
  DEFAULT_QUARTERLY_LIMIT,
  MAX_QUARTERLY_LIMIT,
  MAX_ANNUAL_LIMIT,
  safeSlice,
  getApiPeriodParam,
} from "./shared/financialPeriodConfig";
import GenericFinancialBarChart from "./shared/GenericFinancialBarChart";
import GrowthMetricsDisplay from "./shared/GrowthMetricsDisplay";
import ChartTooltip from "./shared/ChartTooltip";

dayjs.extend(quarterOfYear);

const formatLargeNumber = (value: number): string => {
  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  if (absValue >= 1e9) return `${sign}${(absValue / 1e9).toFixed(1)}B`;
  if (absValue >= 1e6) return `${sign}${(absValue / 1e6).toFixed(1)}M`;
  if (absValue >= 1e3) return `${sign}${(absValue / 1e3).toFixed(1)}K`;
  if (absValue >= 100) return `${sign}${absValue.toFixed(0)}`;
  return `${sign}${absValue.toFixed(2)}`;
};

interface EPSChartProps {
  ticker: string;
  height?: number;
  isModal?: boolean;
  onCloseModal?: () => void;
  renderCard?: boolean;
  showFullscreen?: boolean;
  noHover?: boolean;
  viewMode?: ViewMode;
  limit?: number;
}

const EPSChart: React.FC<EPSChartProps> = ({
  ticker,
  height = 400,
  isModal = false,
  onCloseModal,
  renderCard = true,
  showFullscreen = false,
  noHover = false,
  viewMode: viewModeProp,
  limit: limitProp,
}) => {
  const theme = useTheme();
  const { currencySymbol } = useStockCurrency();
  const colors = getChartColors(theme.palette.mode === "dark");

  const [internalViewMode] = useState<ViewMode>("quarterly");
  const [internalLimit] = useState(DEFAULT_QUARTERLY_LIMIT);

  const effectiveViewMode = viewModeProp ?? internalViewMode;
  const effectiveLimit = limitProp ?? internalLimit;

  const period = getApiPeriodParam(effectiveViewMode);
  const maxLimit = effectiveViewMode === "quarterly" ? MAX_QUARTERLY_LIMIT : MAX_ANNUAL_LIMIT;
  const { data, isLoading, error } = useCompanyIncomeStatement(ticker, maxLimit, period);

  const chartData = useMemo<TransformedDataPoint[]>(() => {
    if (!data || data.length === 0) return [];

    const validData = data.filter((item) => item.epsActual !== null);

    if (effectiveViewMode === "quarterly") {
      const sortedData = validData.sort(
        (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix()
      );

      const periodCounts = new Map<string, number>();

      const allChartData: TransformedDataPoint[] = sortedData.map((item) => {
        const basePeriod = `Q${dayjs(item.date).quarter()} ${dayjs(item.date).year()}`;
        const count = periodCounts.get(basePeriod) ?? 0;
        periodCounts.set(basePeriod, count + 1);
        const periodLabel = count > 0 ? `${basePeriod} (${count + 1})` : basePeriod;

        return {
          period: periodLabel,
          eps: item.epsActual!,
          date: item.date,
          year: dayjs(item.date).year(),
          quarter: dayjs(item.date).quarter(),
        };
      });

      return safeSlice(allChartData, effectiveLimit);
    }

    const sortedData = validData.sort(
      (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix()
    );

    const allChartData: TransformedDataPoint[] = sortedData.map((item) => ({
      period: dayjs(item.date).year().toString(),
      eps: item.epsActual!,
      date: item.date,
      year: dayjs(item.date).year(),
      quarter: null,
    }));

    return safeSlice(allChartData, effectiveLimit);
  }, [data, effectiveViewMode, effectiveLimit]);

  const growthMetrics = useMemo(() => {
    if (chartData.length === 0) return [];
    return effectiveViewMode === "quarterly"
      ? calculateQuarterlyGrowthMetrics(chartData, "eps")
      : calculateAnnualGrowthMetrics(chartData, "eps", true);
  }, [chartData, effectiveViewMode]);

  const minValue = useMemo((): number | "auto" => {
    if (chartData.length === 0) return "auto";
    const epsValues = chartData.map((item) => item.eps as number);
    const minEPS = Math.min(...epsValues);
    if (minEPS >= 0) return "auto";
    const maxEPS = Math.max(...epsValues);
    const range = maxEPS - minEPS;
    const padding = range * 0.1;
    return minEPS - padding;
  }, [chartData]);

  return (
    <GenericFinancialBarChart
      title="Earnings Per Share (EPS)"
      ticker={ticker}
      height={height}
      isModal={isModal}
      renderCard={renderCard}
      showFullscreen={showFullscreen}
      noHover={noHover}
      onCloseModal={onCloseModal}
      isLoading={isLoading}
      error={error}
      chartData={chartData}
      viewMode={effectiveViewMode}
      barKeys={["eps"]}
      colors={(bar: { data: Record<string, number> }) =>
        (bar.data.eps ?? 0) >= 0 ? colors.positive : colors.negative
      }
      yAxisLegend="EPS"
      yAxisFormat={(value) =>
        `${value < 0 ? "-" : ""}${currencySymbol}${formatLargeNumber(Math.abs(value))}`
      }
      minValue={minValue}
      noDataTitle="No EPS Data Available"
      noDataDescription="Earnings per share data is not available for this company, which is common for smaller companies, recent IPOs, or international stocks."
      tooltipContent={({ indexValue, value, color }) => {
        const periodKey = indexValue as string;
        const v = value as number;
        return (
          <ChartTooltip
            header={periodKey}
            headerColor={color}
            rows={[{ label: "EPS", value: `${currencySymbol}${v.toFixed(2)}` }]}
          />
        );
      }}
      growthMetrics={
        <GrowthMetricsDisplay metrics={growthMetrics} />
      }
    />
  );
};

export default EPSChart;
